import { useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, X } from 'lucide-react';
import { useAudioStore } from '../../stores/audioStore';
import { Visualizer } from './Visualizer';

export function AudioPlayer() {
  const {
    currentSong,
    isPlaying,
    volume,
    seek,
    duration,
    togglePlayPause,
    setVolume,
    setSeek,
    playlist,
    playlistIndex,
    isPlaylistMode,
    stopPlaylist,
  } = useAudioStore();

  const progressRef = useRef<HTMLDivElement>(null);

  // Don't render if no song is playing
  if (!currentSong) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !duration) return;

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newSeek = percentage * duration;

    setSeek(newSeek);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  };

  const progress = duration > 0 ? (seek / duration) * 100 : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-monster-card border-t border-monster-border z-50">
      <div className="max-w-7xl mx-auto px-4 py-3">
        {/* Progress Bar */}
        <div
          ref={progressRef}
          onClick={handleProgressClick}
          className="w-full h-1 bg-gray-700 rounded-full cursor-pointer mb-3 overflow-hidden"
        >
          <div
            className="h-full bg-monster-blue transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex items-center gap-4">
          {/* Album Art */}
          <img
            src={currentSong.imagePath}
            alt={currentSong.title}
            className="w-14 h-14 rounded object-cover"
          />

          {/* Song Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold truncate">{currentSong.title}</h3>
              {isPlaylistMode && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-monster-blue/20 text-monster-blue font-medium whitespace-nowrap">
                  Track {playlistIndex + 1}/{playlist.length}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 truncate">{currentSong.artist}</p>
          </div>

          {/* Visualizer */}
          <div className="hidden md:block">
            <Visualizer />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            {/* Time */}
            <div className="text-sm text-gray-400 tabular-nums hidden sm:block">
              {formatTime(seek)} / {formatTime(duration)}
            </div>

            {/* Stop Playlist Button */}
            {isPlaylistMode && (
              <button
                onClick={stopPlaylist}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-monster-crimson hover:bg-red-700 transition-colors"
                title="Stop Playlist"
              >
                <X size={24} />
              </button>
            )}

            {/* Play/Pause */}
            <button
              onClick={togglePlayPause}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-monster-blue hover:bg-blue-600 transition-colors"
            >
              {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-0.5" />}
            </button>

            {/* Volume */}
            <div className="hidden md:flex items-center gap-2">
              {volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-24 accent-monster-blue"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
